import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

export const LXP_CATEGORIES = [
  "general_public",
  "employee",
  "employer_hr",
  "business_owner",
  "llb_student",
  "recent_graduate",
  "candidate_attorney",
  "attorney",
  "advocate",
  "law_firm",
  "recruiter",
  "compliance_professional",
  "academic_institution",
  "lecturer",
  "ngo",
  "government",
  "corporate_legal",
  "other",
] as const;

export const LXP_CATEGORY_LABELS: Record<(typeof LXP_CATEGORIES)[number], string> = {
  general_public: "General Public",
  employee: "Employee",
  employer_hr: "Employer / HR Professional",
  business_owner: "Business Owner / SME",
  llb_student: "LLB Student",
  recent_graduate: "Recent LLB Graduate",
  candidate_attorney: "Candidate Attorney",
  attorney: "Attorney",
  advocate: "Advocate",
  law_firm: "Law Firm",
  recruiter: "Recruiter",
  compliance_professional: "Compliance Professional",
  academic_institution: "Academic Institution",
  lecturer: "Lecturer",
  ngo: "NGO / Community Organisation",
  government: "Government",
  corporate_legal: "Corporate Legal Department",
  other: "Other",
};

// Map a category to the LXP permission role that best fits it.
export const CATEGORY_TO_ROLE: Record<
  (typeof LXP_CATEGORIES)[number],
  "lxp_student" | "lxp_recruiter" | "lxp_university"
> = {
  general_public: "lxp_student",
  employee: "lxp_student",
  employer_hr: "lxp_recruiter",
  business_owner: "lxp_recruiter",
  llb_student: "lxp_student",
  recent_graduate: "lxp_student",
  candidate_attorney: "lxp_student",
  attorney: "lxp_recruiter",
  advocate: "lxp_recruiter",
  law_firm: "lxp_recruiter",
  recruiter: "lxp_recruiter",
  compliance_professional: "lxp_student",
  academic_institution: "lxp_university",
  lecturer: "lxp_university",
  ngo: "lxp_student",
  government: "lxp_student",
  corporate_legal: "lxp_recruiter",
  other: "lxp_student",
};

export const getMyLxpCategory = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data } = await context.supabase
      .from("profiles")
      .select("lxp_category, lxp_onboarded_at")
      .eq("id", context.userId)
      .maybeSingle();
    return {
      category: (data?.lxp_category ?? null) as (typeof LXP_CATEGORIES)[number] | null,
      onboardedAt: data?.lxp_onboarded_at ?? null,
    };
  });

export const setMyLxpCategory = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => z.object({ category: z.enum(LXP_CATEGORIES) }).parse(i))
  .handler(async ({ data, context }) => {
    const { supabase, userId } = context;
    const { error } = await supabase
      .from("profiles")
      .update({ lxp_category: data.category, lxp_onboarded_at: new Date().toISOString() })
      .eq("id", userId);
    if (error) throw new Error(error.message);

    // Also grant the mapped LXP permission role for access control.
    const role = CATEGORY_TO_ROLE[data.category];
    await supabase.from("user_roles").upsert(
      { user_id: userId, role },
      { onConflict: "user_id,role" },
    );

    return { ok: true, category: data.category, role };
  });
